import { ComboBox, IconButton, IDropdownOption, Label, PrimaryButton, TextField } from '@fluentui/react';
import { area, booleanContains, buffer, difference, polygon } from '@turf/turf';
import convert from 'color-convert';
import * as L from 'leaflet';
import { LatLngExpression } from 'leaflet';
import 'leaflet.pattern/dist/leaflet.pattern';
import * as React from 'react';
import { GeoJSON, MapContainer, Marker, Polyline, Popup, TileLayer, Tooltip } from 'react-leaflet';
import { Market } from '../../api/markets.types';
import { AddressInput } from '../../components/core/AddressInput';
import { DocumentTitle } from '../../components/core/DocumentTitle';
import { OkCancelDialog } from '../../components/core/OkCancelDialog';
import { AsyncData } from '../../redux/core';
import { Area } from '../../redux/market-map/types';
import { ToastParams } from '../../redux/message/types';
import { generateColors } from '../../util/Color';
import styles from './MarketMap.module.scss';
import { StripePatternFactory } from './StripePatternFactory';
import crc32 from 'crc32';
import { IMAGES } from '../../config/images';
import { CENTERWELL_CENTERS } from '../../config/constants';

export interface StateProps {
  loadedAreaList: Area[];
  loadedAreasById: Record<string, Area>;
  markets: AsyncData<Market[]>;
  allMarkets: AsyncData<Market[]>;
  lastMapLocation: { lat: number; lng: number };
}

export interface DispatchProps {
  actions: {
    toast: (params: ToastParams) => void;
    viewingBoundsDidChange: (newBounds: any) => void;
    patchArea: (area: Partial<Area>) => void;
    createArea: (area: Area) => void;
    deleteArea: (area: Area) => void;
    setLastMapLocation: (data: { lat: number; lng: number }) => void;
  };
}

type MarketMapProps = StateProps & DispatchProps;

const defaultZoom = 11;
const lowestAvailableZoom = 9;
const snapPixelDistance = 15;

export interface LayerState {
  name: string;
  visible: boolean;
  areaManipulation: boolean;
  editableNames: boolean;
  displayName: string;
}

export interface MarketMapState {
  refreshAreaId?: string;
  currentZoom: number;
  selectedArea?: Area;
  selectedLayer: string;
  layerStates: Record<string, LayerState>;
  drawingNewShape?: LatLngExpression[];
  potentialNextPoint?: LatLngExpression;
  activeMarker?: LatLngExpression;
  marketColors?: { [marketCode: string]: string };
}

export class MarketMap extends React.Component<MarketMapProps, MarketMapState> {
  private mapElement: L.Map | undefined;

  private largestContainingViewPolygon: any;

  private patternMap: any;

  constructor(props: MarketMapProps) {
    super(props);
    this.patternMap = {};

    this.state = {
      refreshAreaId: undefined,
      currentZoom: defaultZoom,
      selectedLayer: 'zipcode',
      layerStates: {
        state: {
          name: 'state',
          visible: false,
          displayName: 'State Coverage',
          areaManipulation: false,
          editableNames: false,
        },
        zipcode: {
          name: 'zipcode',
          visible: true,
          displayName: 'Zipcode Coverage',
          areaManipulation: false,
          editableNames: false,
        },
        vip_inclusion: {
          name: 'vip_inclusion',
          displayName: 'VIP Included Areas',
          visible: true,
          areaManipulation: true,
          editableNames: true,
        },
        extended_market: {
          name: 'extended_market',
          displayName: 'Extended Market',
          visible: true,
          areaManipulation: true,
          editableNames: true,
        },
        blacklisted_areas: {
          name: 'blacklisted_areas',
          displayName: 'Blocked Areas',
          visible: true,
          areaManipulation: true,
          editableNames: true,
        },
        centerwell_centers: {
          name: 'centerwell_centers',
          displayName: 'CenterWell Centers',
          visible: true,
          areaManipulation: false,
          editableNames: false,
        },
      },
    };
  }

  componentDidUpdate() {
    const markets = this.props.allMarkets.data;

    if (markets && !this.state.marketColors) {
      const marketsByGroup: { [groupCode: string]: string[] } = {};
      const marketColors: { [marketCode: string]: string } = {};

      // Group markets by groupCode
      markets.forEach((market) => {
        marketsByGroup[market.groupCode] ? marketsByGroup[market.groupCode].push(market.code) : (marketsByGroup[market.groupCode] = [market.code]);
      });

      // Generate color palette for each market group
      Object.entries(marketsByGroup).forEach(([groupCode, markets]) => {
        // Deterministically generate seedColor for local palette from groupCode
        const seedHue = parseInt(crc32(groupCode), 16) % 360;
        const seedColor = { h: seedHue, s: 75, l: 50 };
        const colors = generateColors(markets.length, seedColor);

        // Assign color from local palette to each market
        markets.forEach((market, index) => {
          const color = colors[index];
          marketColors[market] = '#' + convert.hsl.hex([color.h, color.s, color.l]);
        });
      });

      this.setState({ marketColors });
    }
  }

  public getPatternForColor(patternName: string, color: string) {
    if (this.patternMap[patternName + '/' + color]) {
      return this.patternMap[patternName + '/' + color];
    }

    let pattern: any = null;

    if (patternName === 'diagonal') {
      pattern = StripePatternFactory.generateDiagonalWithColor(color);
    } else if (patternName === 'thickhorizontal') {
      pattern = StripePatternFactory.generateThickHorizontalWithColor(color);
    } else if (patternName === 'thickvertical') {
      pattern = StripePatternFactory.generateThickVerticalWithColor(color);
    }

    this.patternMap[patternName + '/' + color] = pattern;

    if (this.mapElement) {
      pattern.addTo(this.mapElement);
    }

    return pattern;
  }

  public render(): JSX.Element {
    const isTooZoomedOut = this.mapElement ? this.mapElement.getZoom() < lowestAvailableZoom : false;

    return (
      <div className={styles.mapContainer}>
        <DocumentTitle title="Markets Map" />
        {this.renderMap()}
        {isTooZoomedOut && this.renderZoomLimitWarning()}
        <div className={styles.mapControls}>
          {this.renderGeometryEditingControls()}
          <div className="flex-column">
            {this.renderAddressSearch()}
            {this.renderLayerControls()}
            {this.renderSelectedAreaDetailsPane()}
          </div>
        </div>
      </div>
    );
  }

  private onMoveEnd = (event: L.LeafletEvent) => {
    this.viewingBoundsChanged(this.getGeojsonPolygonOfViewingBounds());
    this.props.actions.setLastMapLocation(event.target.getCenter());
  };

  private onZoomEnd = () => {
    this.setState({ currentZoom: this.mapElement!.getZoom() });
  };

  private viewingBoundsChanged(geoJsonBounds: any) {
    if (this.mapElement!.getZoom() < lowestAvailableZoom) {
      // never request new bounds if we're too zoomed out
      return;
    }

    const newViewingBounds = polygon(geoJsonBounds.geometry.coordinates);

    // check if new area is already contained by old one
    if (this.largestContainingViewPolygon && booleanContains(this.largestContainingViewPolygon, newViewingBounds)) {
      return;
    }

    // lets see if we really need to reload - how much now area as a % of the existing area
    // is desired?
    if (this.largestContainingViewPolygon) {
      const differencePolygon = difference(newViewingBounds, this.largestContainingViewPolygon);
      const percentNewAreaOldViewDoesntHave = area(differencePolygon!.geometry as any) / area(this.largestContainingViewPolygon.geometry as any);

      if (percentNewAreaOldViewDoesntHave < 0.1) {
        return;
      }
    }

    // lets calculate a buffer so the true new bounds are actually larger than requested
    this.largestContainingViewPolygon = buffer(newViewingBounds, 20, { units: 'miles' });

    this.props.actions.viewingBoundsDidChange(this.largestContainingViewPolygon);
  }

  private pointToArray(latLngPoint: L.LatLng): number[] {
    return [latLngPoint.lng, latLngPoint.lat];
  }

  private getGeojsonPolygonOfViewingBounds() {
    const bounds = this.mapElement!.getBounds();

    return {
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            this.pointToArray(bounds.getNorthWest()),
            this.pointToArray(bounds.getNorthEast()),
            this.pointToArray(bounds.getSouthEast()),
            this.pointToArray(bounds.getSouthWest()),
            this.pointToArray(bounds.getNorthWest()),
          ],
        ],
      },
    };
  }

  private calculateColor = (area: Area) => {
    if (this.isBlacklisted(area)) {
      return '#FF0000';
    }

    if (area.properties.market_code) {
      const color = this.state.marketColors?.[area.properties.market_code];

      if (color) {
        return color;
      }
    }
    return 'rgba(0,0,0,0)';
  };

  private getLayerForArea(area: Area): LayerState | undefined {
    if (area.properties.layer && this.state.layerStates[area.properties.layer]) {
      return this.state.layerStates[area.properties.layer];
    }
    return undefined;
  }

  private renderArea(area: Area): JSX.Element | null {
    let dashSetting = '0';
    const areasLayer = this.getLayerForArea(area);
    if (areasLayer && !areasLayer.visible) {
      return null;
    }

    if (!area.properties.market_code && !this.isBlacklisted(area)) {
      dashSetting = '10';
    }

    let weightSetting = 1;
    let keyAppend = String(area.version);

    if (this.state.selectedArea && area.id === this.state.selectedArea.id) {
      weightSetting = 5;
      keyAppend = Date.now().toString();
    }

    let pattern: any = null;
    if (area.properties.serviceability !== null && area.properties.serviceability !== '' && area.properties.serviceability !== undefined) {
      if (area.properties.serviceability === '0') {
        pattern = null;
      } else if (area.properties.serviceability === '86400') {
        pattern = 'diagonal';
      } else if (area.properties.serviceability === '172800') {
        pattern = 'thickhorizontal';
      }
    } else if (area.properties.market_code != null) {
      pattern = 'thickvertical';
    }

    return (
      <GeoJSON
        eventHandlers={{
          mouseover: this.onAreaMouseOver,
          mouseout: this.onAreaMouseOut,
          click: this.onAreaClick,
        }}
        key={area.id + '_' + keyAppend}
        data={area as any}
        style={
          {
            weight: weightSetting,
            dashArray: dashSetting,
            fillColor: this.calculateColor(area),
            fillOpacity: 0.45,
            fillPattern: pattern ? this.getPatternForColor(pattern, this.calculateColor(area)) : null,
            color: 'rgb(0,0,0,.5)',
          } as any
        }
      >
        <Tooltip>
          {area.name} ({area.properties.market_code ? area.properties.market_code + ', ' : ''}
          {areasLayer ? areasLayer.displayName : 'Unknown layer'})
        </Tooltip>
      </GeoJSON>
    );
  }

  private onSelectedAreaDetailsCloseClick = () => {
    this.setState({
      selectedArea: undefined,
    });
  };

  private onAreaMouseOver = (event: L.LeafletMouseEvent) => {
    const geoJsonElement = event.target;

    const id = event.propagatedFrom.feature.id;
    if (!this.state.selectedArea || id !== this.state.selectedArea.id) {
      geoJsonElement.setStyle({ weight: 2 });
    }
  };

  private onAreaMouseOut = (event: L.LeafletMouseEvent) => {
    const geoJsonElement = event.target;

    const id = event.propagatedFrom.feature.id;
    if (!this.state.selectedArea || id !== this.state.selectedArea.id) {
      geoJsonElement.setStyle({ weight: 1 });
    }
  };

  private onAreaClick = (event: L.LeafletMouseEvent) => {
    if (this.state.drawingNewShape) {
      return;
    }

    const id = event.propagatedFrom.feature.id;
    if (this.state.selectedArea && this.state.selectedArea.id === id) {
      // area is already selected
      return;
    }
    const clickedArea: Area = this.props.loadedAreasById[id];

    const layersArea = this.getLayerForArea(clickedArea);

    // i want to clone clicked area so that i can tweak its properties without modifying the data
    this.setState({
      selectedLayer: layersArea ? layersArea.name : this.state.selectedLayer,
      selectedArea: this.clone(clickedArea),
    });
  };

  private clone(object: any): any {
    return JSON.parse(JSON.stringify(object));
  }

  private renderMap(): JSX.Element | null {
    let shapeToDraw = this.state.drawingNewShape;

    if (shapeToDraw && shapeToDraw.length >= 1) {
      // only add the mouse following 'the next line will look like this'
      // point if we've placed the first point
      shapeToDraw = shapeToDraw.slice();
      shapeToDraw.push(this.state.potentialNextPoint as any);
    }

    const icon = new L.Icon({ iconUrl: IMAGES.mapCenterWell, iconSize: [34, 37] });

    return (
      <div className={shapeToDraw ? 'map-cursor-crosshair map-grayscale' : 'map-grayscale'} style={{ height: '100%', width: '100%' }}>
        <MapContainer
          whenCreated={(map) => {
            this.mapElement = map;
            map.addEventListener('zoomend', this.onZoomEnd);
            map.addEventListener('moveend', this.onMoveEnd);
            map.addEventListener('mousemove', this.onMouseMove);
            map.addEventListener('click', this.onMapClick);
            map.addEventListener('keyup', this.onKeyUp);
            this.viewingBoundsChanged(this.getGeojsonPolygonOfViewingBounds());
          }}
          center={this.props.lastMapLocation}
          zoom={defaultZoom}
        >
          <TileLayer attribution={`&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors`} url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {shapeToDraw && <Polyline dashArray="20" color="black" positions={shapeToDraw} />}

          {this.state.currentZoom >= lowestAvailableZoom &&
            this.props.loadedAreaList.map((area) => {
              if (this.state.selectedArea && area.id === this.state.selectedArea.id) {
                return undefined;
              }
              return this.renderArea(area);
            })}
          {this.state.selectedArea && this.renderArea(this.state.selectedArea)}
          {this.state.activeMarker && <Marker position={this.state.activeMarker}></Marker>}
          {this.state.layerStates['centerwell_centers'].visible &&
            CENTERWELL_CENTERS.map((center) => (
              <Marker key={center.id} icon={icon} position={center.coordinates}>
                <Popup>
                  <div>
                    <b>{center.name}</b>
                    <p>{center.address}</p>
                  </div>
                </Popup>
              </Marker>
            ))}
        </MapContainer>
      </div>
    );
  }

  private onKeyUp = (event: L.LeafletKeyboardEvent) => {
    if (event.type === 'keyup') {
      this.setState({ drawingNewShape: undefined, selectedArea: undefined });
    }
  };

  private onMapClick = (event: L.LeafletMouseEvent) => {
    if (!this.state.drawingNewShape) {
      return;
    }
    const shapeWithNewPoint: L.LatLngExpression[] = this.state.drawingNewShape.slice();
    shapeWithNewPoint.push(this.suggestPoint(event.latlng) as any);

    const startingPoint: any = shapeWithNewPoint[0];
    const endingPoint: any = shapeWithNewPoint[shapeWithNewPoint.length - 1];

    if (shapeWithNewPoint.length > 2 && startingPoint.lat === endingPoint.lat && startingPoint.lng === endingPoint.lng) {
      const newGeoJsonArea: Area = {
        id: new Date().toString(), // need some kind of id for rendering, but its local
        version: undefined,
        type: 'Feature',
        name: '',
        properties: {
          market_code: undefined,
          layer: this.state.selectedLayer,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [shapeWithNewPoint.map((p: any) => [p.lng, p.lat])],
        },
      };

      this.setState({ selectedArea: newGeoJsonArea, drawingNewShape: undefined });
    } else {
      this.setState({ drawingNewShape: shapeWithNewPoint });
    }
  };

  private onMouseMove = (event: any) => {
    if (this.state.drawingNewShape) {
      this.setState({ potentialNextPoint: this.suggestPoint(event.latlng) });
    }
  };

  private getSuggestionPointsFromLoadedAreas = (): LatLngExpression[] => {
    const pointsToCheck: LatLngExpression[] = [];
    for (let i = 0; i < this.props.loadedAreaList.length; i++) {
      if (!this.state.layerStates[this.props.loadedAreaList[i].properties.layer].areaManipulation) {
        continue;
      }
      // geojson Polygons store their points in an array with one element, hence [0]
      // note that MultiPolygons store this differently, but only zipcodes use those.
      for (let j = 0; j < this.props.loadedAreaList[i].geometry.coordinates[0].length; j++) {
        pointsToCheck.push({
          lng: this.props.loadedAreaList[i].geometry.coordinates[0][j][0],
          lat: this.props.loadedAreaList[i].geometry.coordinates[0][j][1],
        });
      }
    }

    return pointsToCheck;
  };

  private suggestPoint = (point: LatLngExpression): LatLngExpression => {
    let pointsToCheck: LatLngExpression[] = [];
    if (this.state.drawingNewShape && this.state.drawingNewShape.length > 0) {
      pointsToCheck.push(this.state.drawingNewShape[0]);
    }

    pointsToCheck = pointsToCheck.concat(this.getSuggestionPointsFromLoadedAreas());

    for (let i = 0; i < pointsToCheck.length; i++) {
      const mousePointXY = this.mapElement!.latLngToContainerPoint(point);
      const potentialPointXY = this.mapElement!.latLngToContainerPoint(pointsToCheck[i]);

      const dist = Math.sqrt(Math.pow(mousePointXY.x - potentialPointXY.x, 2) + Math.pow(mousePointXY.y - potentialPointXY.y, 2));

      if (dist < snapPixelDistance) {
        return pointsToCheck[i];
      }
    }

    return point;
  };

  private marketsForComboBox(markets?: Market[]): IDropdownOption[] {
    let options: IDropdownOption[] = [{ key: '', text: 'No Market' }];

    if (markets) {
      options = options.concat(markets.map((m) => ({ key: m.code, text: `${m.name} (${m.code})` })));
    }
    return options;
  }

  private saveSelectedAreaChanges = () => {
    const isNewArea = this.state.selectedArea!.version === undefined;

    if (!this.state.selectedArea!.name) {
      this.props.actions.toast({
        title: 'Error',
        content: 'You must provide a name for this area',
        type: 'error',
      });
      return;
    }

    const areaToSave = this.clone(this.state.selectedArea!);
    if (areaToSave.properties.serviceability === '') {
      areaToSave.properties.serviceability = null;
    }
    if (areaToSave.properties.market_code === '') {
      areaToSave.properties.market_code = null;
    }

    if (isNewArea) {
      this.props.actions.createArea(areaToSave);
    } else {
      const areaToPatch: Partial<Area> = areaToSave;
      areaToPatch.geometry = undefined; // dont bother sending this up for patch reqs
      this.props.actions.patchArea(areaToPatch);
    }

    this.setState({
      selectedArea: undefined,
    });
  };

  private servicabilityLevels(): IDropdownOption[] {
    return [
      { key: '', text: 'No Service' },
      { key: '0', text: 'Same Day' },
      { key: '86400', text: '24 hours in advance' },
      { key: '172800', text: '48 hours in advance' },
    ];
  }

  private renderSelectedAreaDetailsPane(): JSX.Element {
    if (!this.state.selectedArea) {
      return <></>;
    }
    const selectedArea = this.state.selectedArea;
    const currentLayer = this.state.layerStates[selectedArea.properties.layer];

    return (
      <div className={styles.selectedAreaDetailsContainer}>
        <div style={{ display: 'flex', flexFlow: 'row wrap', justifyContent: 'space-between' }}>
          <TextField
            label="Name"
            autoFocus
            disabled={currentLayer && !currentLayer.areaManipulation}
            value={selectedArea.name}
            onChange={(evt, value) => {
              const newArea = this.clone(this.state.selectedArea!);
              newArea.name = value;

              this.setState({
                selectedArea: newArea,
              });
            }}
          />

          <IconButton onClick={this.onSelectedAreaDetailsCloseClick} iconProps={{ iconName: 'Cancel' }} />
        </div>

        <ComboBox
          label="Market"
          disabled={this.isBlacklisted(selectedArea)}
          selectedKey={selectedArea.properties.market_code || ''}
          allowFreeform={false}
          onChange={(evt, option) => {
            const newArea = this.clone(this.state.selectedArea!);
            newArea.properties.market_code = option!.key;
            this.setState({
              selectedArea: newArea,
            });
          }}
          options={this.marketsForComboBox(this.props.markets.data)}
        />
        <ComboBox
          label="Serviceability"
          disabled={this.isBlacklisted(selectedArea)}
          selectedKey={selectedArea.properties.serviceability || ''}
          allowFreeform={false}
          onChange={(evt, option) => {
            const newArea = this.clone(this.state.selectedArea!);
            newArea.properties.serviceability = option!.key;
            this.setState({
              selectedArea: newArea,
              refreshAreaId: newArea.id,
            });
          }}
          options={this.servicabilityLevels()}
        />
        <TextField disabled={true} label="Layer" value={selectedArea.properties.layer} />
        <PrimaryButton style={{ marginTop: '10px' }} text="Save" onClick={this.saveSelectedAreaChanges} allowDisabledFocus={true} />
      </div>
    );
  }

  private onLayerVisibilityClick = (layerClicked: LayerState) => {
    layerClicked.visible = !layerClicked.visible;

    this.setState({
      layerStates: this.clone(this.state.layerStates),
    });
  };

  private onLayerClick = (layerClicked: LayerState) => () => {
    this.setState({
      selectedLayer: layerClicked.name,
      selectedArea: undefined,
    });
  };

  private renderLayerControls(): JSX.Element {
    return (
      <div className={styles.layerContainer}>
        <div style={{ padding: '8px' }}>
          <Label>Layers</Label>
        </div>
        {Object.keys(this.state.layerStates).map((layerKey) => {
          const l: LayerState = this.state.layerStates[layerKey];

          const isSelected = l.name === this.state.selectedLayer;

          return (
            <div
              key={l.name}
              onClick={this.onLayerClick(l)}
              className={styles.layer}
              style={{
                backgroundColor: isSelected ? 'lightgray' : undefined,
              }}
            >
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  this.onLayerVisibilityClick(l);
                }}
                style={{ color: l.visible ? undefined : 'gray' }}
                iconProps={{ iconName: l.visible ? 'RedEye' : 'Hide' }}
              />
              <Label style={{ cursor: 'pointer' }}>{l.displayName}</Label>
            </div>
          );
        })}
      </div>
    );
  }

  private onDeleteAreaClicked = () => {
    const hasBeenSaved = this.state.selectedArea!.version !== undefined;

    OkCancelDialog.show({
      title: 'Delete Area',
      subText: 'Are you sure you want to delete this area?',
      onOk: () => {
        if (hasBeenSaved) {
          this.props.actions.deleteArea(this.state.selectedArea!);
        }
        this.setState({
          selectedArea: undefined,
        });
      },
    });
  };

  private onAddressSelect = (address?: any) => {
    if (address && this.mapElement) {
      const destination = { lat: address.latitude, lng: address.longitude };
      this.mapElement.setView(destination, defaultZoom);
      this.setState({ activeMarker: destination });
    }
  };

  private renderAddressSearch = (): JSX.Element => {
    return (
      <div className="ms-depth-8" style={{ marginBottom: '17px', height: '32px' }}>
        <AddressInput onSelect={this.onAddressSelect} />
      </div>
    );
  };

  private isBlacklisted(area: Area) {
    return area.properties.layer === 'blacklisted_areas';
  }

  private renderGeometryEditingControls(): JSX.Element {
    const selectedLayer = this.state.layerStates[this.state.selectedLayer];
    if (!selectedLayer.areaManipulation) {
      return <></>;
    }
    return (
      <div style={{ marginRight: '20px' }} className="flex-row">
        <div className={styles.geometryEditingControlsContainer}>
          <IconButton onClick={() => this.setState({ drawingNewShape: [] })} iconProps={{ iconName: 'Add' }} />
        </div>
        <div
          className="ms-depth-8"
          style={{
            backgroundColor: 'white',
          }}
        >
          <IconButton disabled={this.state.selectedArea === undefined} onClick={this.onDeleteAreaClicked} iconProps={{ iconName: 'Delete' }} />
        </div>
      </div>
    );
  }

  private renderZoomLimitWarning(): JSX.Element {
    return <div className={styles.zoomLimitWarning}>Market visualization is not supported at this zoom level.</div>;
  }
}
